import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ConfirmEmailChangeDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  currentEmail: string;
  newEmail: string;
}

const ConfirmEmailChangeDialog: React.FC<ConfirmEmailChangeDialogProps> = ({
  isOpen,
  onCancel,
  onConfirm,
  isLoading = false,
  currentEmail,
  newEmail,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'teams' });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={isLoading ? undefined : onCancel}
      />
      <div className="relative z-[60] w-full max-w-md mx-4 bg-white rounded-xl shadow-2xl">
        <div className="p-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900">{t('confirmEmailChange.title')}</h3>
              <p className="mt-2 text-sm text-gray-600">
                {t('confirmEmailChange.fromTo', { currentEmail, newEmail })}
              </p>
              <ul className="mt-3 text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>{t('confirmEmailChange.consequence1')}</li>
                <li>{t('confirmEmailChange.consequence2')}</li>
                <li>{t('confirmEmailChange.consequence3')}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-3 px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-[#4339A0] rounded-lg hover:bg-[#362d85] transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            <span>{t('confirmEmailChange.confirm')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmailChangeDialog;
